import type React from "react";
import { createElement, useEffect, useState } from "react";

import type { MessageCollapseStatus } from "app/pages/sales-intelligence/modules/ai-agent/components/ChatMessage/types";
import { CollapsedBriefSection } from "app/pages/sales-intelligence/modules/ai-agent/components/CollapsedBriefSection/CollapsedBriefSection";
import { StyledHr } from "app/pages/sales-intelligence/modules/ai-agent/components/markdown-components";

export const useStructuredBrief = ({
    collapseStatus = "expanded",
    setCollapseStatus,
    childrenRef,
    isLoading,
    enabled = true,
}: {
    collapseStatus?: MessageCollapseStatus;
    setCollapseStatus?: (status: MessageCollapseStatus) => void;
    childrenRef: React.RefObject<HTMLDivElement | null>;
    isLoading?: boolean;
    enabled?: boolean;
}) => {
    const [collapsedState, setCollapsedState] = useState<Record<number, boolean>>({});
    const [structuredBrief, setStructuredBrief] = useState<React.ReactNode[] | null>(null);
    const [refChildren, setRefChildren] = useState<Element[]>([]);

    useEffect(() => {
        if (!enabled) {
            setStructuredBrief(null);
            return;
        }
        if (collapseStatus === "collapsed") {
            const newState: Record<number, boolean> = {};
            Object.keys(collapsedState).forEach((key) => {
                newState[Number(key)] = true;
            });
            setCollapsedState(newState);
            return;
        }
        if (collapseStatus === "expanded") {
            const newState: Record<number, boolean> = {};
            Object.keys(collapsedState).forEach((key) => {
                newState[Number(key)] = false;
            });
            setCollapsedState(newState);
            return;
        }
    }, [collapseStatus, enabled]);

    const handleCollapse = (index: number) => (isCollapsed: boolean) => {
        const newState: Record<number, boolean> = { ...collapsedState, [index]: isCollapsed };
        setCollapsedState(newState);

        const values = Object.values(newState);

        if (values.length && values.every((isCollapsed) => isCollapsed)) {
            setCollapseStatus?.("collapsed");
            return;
        }
        if (values.length && values.every((isCollapsed) => !isCollapsed)) {
            setCollapseStatus?.("expanded");
            return;
        }
        if (values.length) {
            setCollapseStatus?.("none");
        }
    };

    const processContent = () => {
        const resultChildren: React.ReactNode[] = [];
        const children = refChildren ?? [];

        let index = 0;
        let sectionCounter = 0;

        while (index < children.length) {
            const currentElement = children[index];

            if (currentElement.tagName === "SPAN" && currentElement.className.includes("meeting-agent-section-title")) {
                const sectionTitle = currentElement;
                const sectionContent: React.ReactNode[] = [];
                let nextIdx = index + 1;

                while (
                    nextIdx < children.length &&
                    children[nextIdx].tagName !== "HR" &&
                    !children[nextIdx].className.includes("meeting-agent-section-title")
                ) {
                    const nextElement = children[nextIdx];
                    sectionContent.push(
                        createElement(nextElement.tagName.toLowerCase(), {
                            className: nextElement.className,
                            dangerouslySetInnerHTML: { __html: nextElement.innerHTML },
                        }),
                    );
                    nextIdx++;
                }
                resultChildren.push(
                    <CollapsedBriefSection
                        title={
                            <span
                                dangerouslySetInnerHTML={{ __html: sectionTitle.innerHTML }}
                                className={sectionTitle.className}
                            />
                        }
                        children={sectionContent}
                        isCollapsed={Boolean(collapsedState[sectionCounter])}
                        onCollapse={handleCollapse(sectionCounter)}
                        trackingName={(sectionTitle as HTMLSpanElement).innerText}
                    />,
                );
                if (collapsedState[sectionCounter] === undefined) {
                    handleCollapse(sectionCounter)(false);
                }

                sectionCounter++;

                if (nextIdx < children.length) {
                    resultChildren.push(<StyledHr />);
                }

                index = nextIdx;
                continue;
            }

            if (currentElement.tagName !== "HR") {
                resultChildren.push(
                    createElement(currentElement.tagName.toLowerCase(), {
                        dangerouslySetInnerHTML: { __html: currentElement.innerHTML },
                        className: currentElement.className,
                    }),
                );
            }

            index++;
        }

        return resultChildren;
    };

    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (childrenRef.current && !isLoading) {
            setRefChildren(Array.from(childrenRef.current.children));
        }
    }, [isLoading, enabled]);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (refChildren?.length) {
            setStructuredBrief(processContent());
        }
    }, [refChildren, collapsedState, enabled]);

    return { structuredBrief };
};
